import { 
  Order, 
  Sender, 
  Payee, 
  OrderUpdate, 
  SenderUpdate, 
  PayeeUpdate, 
  SenderTypeEnum,
  SenderIPUpdate,
  SenderOOOUpdate,
  SenderSelfEmployedUpdate,
  PayeeTypeEnum
} from '../store/types'

/**
 * Extracts only the fields that are valid for order updates
 * @param order The full order object
 * @returns An object with only the fields that can be updated
 */
export const extractOrderUpdateFields = (order: Partial<Order>): OrderUpdate => {
  const { order_type, currency, amount } = order
  const payment_type = order.preferred_contact_method as unknown as PayeeTypeEnum
  const sender_type = order.sender_type as SenderTypeEnum | null
  
  return { 
    order_type, 
    currency, 
    amount, 
    payment_type, 
    sender_type
  }
}

/**
 * Extracts only the fields that are valid for sender updates based on sender type
 * @param sender The full sender object
 * @returns An object with only the fields that can be updated
 */
export const extractSenderUpdateFields = (sender: Sender): SenderUpdate => {
  const baseFields = {
    name: sender.name,
    name_en: sender.name_en,
    inn: sender.inn,
    bank_name: sender.bank_name,
    bank_name_en: sender.bank_name_en,
    bank_bik: sender.bank_bik,
    bank_current_account: sender.bank_current_account,
    bank_correspondent_account: sender.bank_correspondent_account,
    entity_address: sender.entity_address,
    entity_address_en: sender.entity_address_en,
    agreement_number: sender.agreement_number,
    agreement_date: sender.agreement_date,
  }

  switch (sender.type) {
    case SenderTypeEnum.IP:
      return {
        ...baseFields,
        type: SenderTypeEnum.IP,
        ogrnip: sender.ogrnip
      } as SenderIPUpdate
    
    case SenderTypeEnum.OOO:
      return {
        ...baseFields,
        type: SenderTypeEnum.OOO,
        kpp: sender.kpp,
        ceo_name: sender.ceo_name,
        ceo_name_genitive: sender.ceo_name_genitive,
        ceo_name_en: sender.ceo_name_en,
        cfo_name: sender.cfo_name,
        cfo_name_genitive: sender.cfo_name_genitive,
        cfo_name_en: sender.cfo_name_en,
      } as SenderOOOUpdate
    
    case SenderTypeEnum.SELF_EMPLOYED:
      return {
        ...baseFields,
        type: SenderTypeEnum.SELF_EMPLOYED,
        legal_address: sender.legal_address,
        legal_address_en: sender.legal_address_en,
        bank_address: sender.bank_address,
        bank_address_en: sender.bank_address_en,
        email: sender.email,
        phone: sender.phone,
      } as SenderSelfEmployedUpdate
  }
}

/**
 * Extracts only the fields that are valid for payee updates
 * @param payee The full payee object
 * @returns An object with only the fields that can be updated
 */
export const extractPayeeUpdateFields = (payee: Partial<Payee>): PayeeUpdate => {
  const {
    name,
    name_en,
    import_contract_number,
    invoice_number,
    address,
    address_en,
    account,
    bank_name,
    bank_name_en,
    bank_address,
    bank_address_en,
    bank_swift,
    product_items,
    parsed_data_confirmed_by_user
  } = payee

  return {
    name,
    name_en,
    import_contract_number,
    invoice_number,
    address,
    address_en,
    account,
    bank_name,
    bank_name_en,
    bank_address,
    bank_address_en,
    bank_swift,
    product_items,
    parsed_data_confirmed_by_user
  }
} 